import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, CircleAlert, Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { BlockEditorValue } from './BlockEditor';

export type SaveState = 'idle' | 'unsaved' | 'saving' | 'saved' | 'error';

interface PersistCallbacks {
  onSuccess: () => void;
  onError: () => void;
}

/**
 * Autosave controller for a page BlockEditor. Wire `onChange`/`onSave` into the
 * editor and `editorRef` onto it; call `flush()` from a manual Save/Done button.
 * `persist` performs the actual mutation and reports back via the callbacks.
 */
export function useAutosave(persist: (value: BlockEditorValue, cbs: PersistCallbacks) => void) {
  const [state, setState] = useState<SaveState>('idle');
  const dirty = useRef(false);
  const stateRef = useRef<SaveState>('idle');

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const onChange = useCallback(() => {
    dirty.current = true;
    setState((s) => (s === 'saving' ? s : 'unsaved'));
  }, []);

  const onSave = useCallback(
    (value: BlockEditorValue) => {
      dirty.current = false;
      setState('saving');
      persist(value, {
        onSuccess: () => setState('saved'),
        onError: () => {
          dirty.current = true;
          setState('error');
        },
      });
    },
    [persist],
  );

  // Warn before leaving (tab close / reload) while there are unsaved or
  // in-flight edits. In-app route changes flush via the editor's unmount.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirty.current || stateRef.current === 'saving') {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  return { state, onChange, onSave };
}

/** Compact save-status pill shown in the page header while editing, and on
 *  autosaving settings pages (FE-ADR-065). When idle only an empty, hidden live
 *  region is left, so the next state is announced to screen readers. */
export function SaveIndicator({ state, className }: { state: SaveState; className?: string }) {
  if (state === 'idle') return <span role="status" className="sr-only" aria-live="polite" />;
  const map: Record<Exclude<SaveState, 'idle'>, { label: string; icon: React.ReactNode; tone: string }> = {
    unsaved: { label: 'Unsaved changes', icon: <span className="bg-muted-foreground/50 size-1.5 rounded-full" />, tone: 'text-muted-foreground' },
    saving: { label: 'Saving…', icon: <Loader2 className="size-3.5 animate-spin" />, tone: 'text-muted-foreground' },
    saved: { label: 'Saved', icon: <Check className="size-3.5" />, tone: 'text-emerald-600 dark:text-emerald-400' },
    error: { label: 'Save failed', icon: <CircleAlert className="size-3.5" />, tone: 'text-destructive' },
  };
  const { label, icon, tone } = map[state];
  return (
    <span role="status" className={cn('flex items-center gap-1.5 text-xs', tone, className)} aria-live="polite">
      {icon} {label}
    </span>
  );
}
